import Flickity from "flickity-imagesloaded";
import React from "react";
import { MiniConfigurator } from "tsi-common-react/src/apps/configurator/components/MiniConfigurator";
import { LoadingSpinner } from "tsi-common-react/src/common/LoadingSpinner";
import { BundleGroupTypes } from "tsi-common-react/src/constants";
import {
    IConcreteBundle,
    IProduct,
} from "tsi-common-react/src/models/catalogue";
import { t } from "ttag";

import { OutOfContextBundle } from "./OutOfContextBundle";

const FLICKITY_OPTIONS = {
    cellAlign: "left",
    wrapAround: true,
    imagesLoaded: true,
    arrowShape: {
        x0: 10,
        x1: 60,
        y1: 50,
        x2: 60,
        y2: 45,
        x3: 15,
    },
} as const;

function filterRelevantBundles(
    bundles: readonly IConcreteBundle[],
): readonly IConcreteBundle[] {
    return bundles.filter((bundle) => {
        const bundle_type = bundle.bundle_group.bundle_type;
        return (
            bundle_type === BundleGroupTypes.DEFAULT ||
            bundle_type === BundleGroupTypes.PDP_BUY_TOGETHER
        );
    });
}

export const OutOfContextBundleCollection = (props: {
    rootProduct: IProduct | null;
    baseVariant: IProduct | null;
    bundles: readonly IConcreteBundle[];
}): React.ReactNode => {
    const bundlesElemRef = React.useRef<HTMLDivElement | null>(null);
    const flickityRef = React.useRef<Flickity | null>(null);

    React.useEffect(() => {
        const bundlesElem = bundlesElemRef.current;
        if (!bundlesElem) {
            return;
        }

        // Clean up existing Flickity instance
        if (flickityRef.current) {
            flickityRef.current.destroy();
            flickityRef.current = null;
        }

        // Initialize Flickity if we have multiple bundles
        if (bundlesElem.childNodes.length > 1) {
            flickityRef.current = new Flickity(bundlesElem, FLICKITY_OPTIONS);
        }

        // Cleanup function
        return () => {
            if (flickityRef.current) {
                flickityRef.current.destroy();
                flickityRef.current = null;
            }
        };
    }, [props.rootProduct, props.baseVariant, props.bundles]);

    // Show a loading spinner while loading data
    if (!props.rootProduct || !props.baseVariant) {
        return <LoadingSpinner />;
    }

    // If we have a base product but no bundles, don't display anything
    if (props.bundles.length <= 0) {
        return null;
    }

    const rootProduct = props.rootProduct;
    const baseVariant = props.baseVariant;

    const bundles = filterRelevantBundles(props.bundles).map((bundle, i) => {
        return (
            <OutOfContextBundle
                key={i}
                product={rootProduct}
                variant={baseVariant}
                bundle={bundle}
            />
        );
    });

    return (
        <div>
            <div className="pdp-bundles-header">
                <div className="pdp-bundles-header__title">
                    {t`Make Your Bed Even More Dreamy`}
                </div>
                <div className="pdp-bundles-header__desc">
                    {t`Choose a mattress & base combination for unlimited comfort, full-body ergonomic support, and even massage options. Remember: your Tempur-Pedic mattress needs a sturdy base.`}
                </div>
            </div>
            <MiniConfigurator
                rootProduct={rootProduct}
                configType={"pdp-hero"}
            />
            <div className="pdp-bundles__list" ref={bundlesElemRef}>
                {bundles}
            </div>
        </div>
    );
};
